import { useEffect, useMemo } from 'react';

import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import { useSiteContext } from '@/providers';

import {
  connectToApi,
  stopConnectionsExcept,
} from './useMultiSiteConnections/useMultiSiteConnections.functions.ts';
import { BridgeHub } from './useMultiSiteConnections/useMultiSiteConnections.types.ts';

export const useMultiSiteConnections = () => {
  const { sites, areSitesResolved } = useSiteContext();
  const queryClient = useQueryClient();

  const apiUrls = useMemo<BridgeHub[]>(
    () =>
      (sites ?? []).map(s => ({
        id: s.id,
        name: s.name,
        url: s.apis.bridgeApi,
      })),
    [sites]
  );

  // An empty list is normal while the accessible sites are still loading; only
  // an empty list after loading has settled means there is nothing to connect to.
  const hasNoSites = areSitesResolved && sites.length === 0;
  useEffect(() => {
    if (hasNoSites) console.warn('No sites found to connect to.');
  }, [hasNoSites]);

  // Sites removed from the list must not keep a live connection, and their
  // cached query (holding the now-stopped connection) must not be reused if
  // the site comes back. An empty list tears everything down like any other
  // removal: with a default site configured the list is never empty, and in
  // portable mode (no default site) there is nothing to keep alive until
  // inventory delivers the sites.
  useEffect(() => {
    stopConnectionsExcept(apiUrls.map(s => s.id));
    queryClient.removeQueries({
      queryKey: ['bridge-connection'],
      predicate: query =>
        !apiUrls.some(s => query.queryKey[1] === s.id && query.queryKey[2] === s.url),
    });
  }, [apiUrls, queryClient]);

  // One query per site so each connection is exposed as soon as it is
  // established. A slow or unreachable site must not delay the others.
  return useQueries({
    queries: apiUrls.map(s => ({
      queryKey: ['bridge-connection', s.id, s.url] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        connectToApi(s, signal, () => {
          // Established connection dropped for good: refetch this site only.
          void queryClient.invalidateQueries({ queryKey: ['bridge-connection', s.id, s.url] });
        }),
      // Never stale: refetchOnReconnect then retries only failed sites when
      // the browser regains network, without restarting healthy connections.
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false, // let SignalR handle reconnects; we reconnect via onclose
      refetchOnReconnect: true,
    })),
    combine: results => {
      const connections: Record<string, HubConnection> = {};
      const pendingSites: string[] = [];
      results.forEach((result, i) => {
        if (result.data) {
          connections[apiUrls[i].id] = result.data;
        }
        if (result.isPending) {
          pendingSites.push(apiUrls[i].id);
        }
      });
      return {
        connections,
        pendingSites,
        // Loading until the first site has connected or failed. With no site
        // queries yet (portable mode before inventory delivers sites) this is
        // false — same as the previous disabled-query behavior — and bridge
        // subscriptions self-correct once a connection arrives, because their
        // query keys include the connectionId. Consumers track their own site
        // via pendingSites and the connections map.
        isLoading: results.length > 0 && results.every(result => result.isPending),
        refetchDisconnected: () => {
          results.forEach(result => {
            // One reconnect attempt per drop: a query in error state already
            // failed its retry and waits for the browser-reconnect path, so
            // a site that stays offline does not keep looping.
            if (
              result.data?.state === HubConnectionState.Disconnected &&
              !result.isFetching &&
              !result.isError
            ) {
              void result.refetch();
            }
          });
        },
      };
    },
  });
};
